'use client';
import { useEffect, useRef } from 'react';
import Head from 'next/head';
import PropTypes from 'prop-types';
import tailwindConfig from '../../tailwind.config.js';
import { isValidHttpUrl, loadLazyImage } from '@/utils';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { selectIsRobot } from '@/store/index';

/* 
   Generate screen breakpoint list
*/
function screenArray() {
  const screens = [{ key: 'xs', value: tailwindConfig.theme.screens.sm }];
  screens.push(
    ...Object.entries(tailwindConfig.theme.screens).map(([key, value]) => ({
      key,
      value,
    })),
  );
  return screens;
}

/* ------------------------------------
   Resolve image size for breakpoints
------------------------------------ */
function imageSize(sizeObject, breakpoint) {
  if (sizeObject) {
    if (sizeObject[breakpoint]) {
      return sizeObject[breakpoint] > 0 ? sizeObject[breakpoint] : null;
    }
    const sList = screenArray().map((item) => item.key);
    let startIndex = sList.indexOf(breakpoint);
    while (startIndex > -1) {
      startIndex--;
      if (sizeObject[sList[startIndex]]) {
        return sizeObject[sList[startIndex]];
      }
    }
  }
  return null;
}

/* 
   Cloudflare Image URL Builder
*/
function buildCloudflareUrl({ src, width, height, gravity, format, fit, quality = 85 }) {
  const processorUrl = process.env.IMAGE_PROCESSOR_URL;

  if (!processorUrl) {
    throw new Error('NEXT_PUBLIC_IMAGE_PROCESSOR_URL is not set!');
  }

  const base = new URL(processorUrl);
  let relativePath;

  if (isValidHttpUrl(src)) {
    const parsed = new URL(src);
    relativePath = parsed.pathname;
  } else {
    relativePath = src.startsWith('/') ? src : `/${src}`;
  }

  if (relativePath.startsWith('/')) {
    relativePath = relativePath.slice(1);
  }

  const url = new URL(relativePath, base);

  if (width) url.searchParams.set('w', width);
  if (height) url.searchParams.set('h', height);
  if (gravity) url.searchParams.set('g', gravity);
  if (format) url.searchParams.set('format', format);
  if (fit) url.searchParams.set('fit', fit);
  if (quality) url.searchParams.set('quality', quality);

  return url.href;
}

/* 
   Responsive Image Component
*/
const pixelDensities = [1, 2];

const ResponsiveImage = ({
  image,
  widths,
  heights,
  preload,
  cdnProps,
  dataNotLazy,
  imgProps,
  className,
  fit = 'cover',
  quality = 85,
  ...props
}) => {
  const pictureRef = useRef();
  const isRobot = useSelector(selectIsRobot);

  /* Lazy-load observer */
  useEffect(() => {
    let observerRefValue = pictureRef.current;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        loadLazyImage(entry.target);
        observer.unobserve(observerRefValue);
      }
    });

    if (!isRobot) {
      if (observerRefValue?.getBoundingClientRect().top < window.screen.height) {
        loadLazyImage(pictureRef.current);
      } else {
        observerRefValue && observer.observe(observerRefValue);
      }
    }

    return () => observerRefValue && observer.unobserve(observerRefValue);
  }, [isRobot]);

  if (!image) return null;

  const screens = screenArray();

  /* Determine gravity from backend values */
  let gravity = '0.5x0.5';
  if (image.gravity) {
    gravity = image.gravity;
  } else if (image.focalPoint || (image.x !== undefined && image.y !== undefined)) {
    const gx = image.x ?? image.focalPoint?.x ?? 0.5;
    const gy = image.y ?? image.focalPoint?.y ?? 0.5;
    gravity = `${gx}x${gy}`;
  }

  const [gravityX, gravityY] = gravity.split('x').map(parseFloat);
  const objectPosX = isNaN(gravityX) ? 0.5 : gravityX;
  const objectPosY = isNaN(gravityY) ? 0.5 : gravityY;
  const objectPosition = `${objectPosX * 100}% ${objectPosY * 100}%`;

  /* Build srcset for each breakpoint */
  const buildSrcSet = (breakpoint, format) =>
    pixelDensities.map(
      (pd) =>
        buildCloudflareUrl({
          src: image.imageUrl,
          width: imageSize(widths, breakpoint) * pd,
          height: imageSize(heights, breakpoint) * pd,
          gravity,
          format,
          fit,
          quality,
          ...cdnProps,
        }) + ` ${pd}x`,
    );

  const sourceArray = [];
  const preloadLinks = [];

  screens.forEach((screen, i) => {
    let media;
    if (i === 0) media = `(max-width: ${parseInt(screen.value, 10) - 1}px)`;
    else if (i === screens.length - 1) media = `(min-width: ${screen.value})`;
    else media = `(min-width: ${screen.value}) and (max-width: ${parseInt(screens[i + 1].value, 10) - 1}px)`;

    const sizes = pixelDensities.map((pd) => `${pd}x`).join(', ');
    const srcSetWebp = buildSrcSet(screen.key, 'webp');
    const srcSet = buildSrcSet(screen.key);

    if (!image.isSvg) {
      sourceArray.push(
        <source
          key={`source-webp-${screen.key}-${i}`} // unique key
          sizes={sizes}
          {...(preload || dataNotLazy ? { srcSet: srcSetWebp.join(', ') } : { 'data-srcset': srcSetWebp.join(', ') })}
          media={media}
          type="image/webp"
        />,
      );
    }

    sourceArray.push(
      <source
        key={`source-img-${screen.key}-${i}`} // unique key
        sizes={sizes}
        {...(preload || dataNotLazy ? { srcSet: srcSet.join(', ') } : { 'data-srcset': srcSet.join(', ') })}
        media={media}
      />,
    );

    if (preload) {
      preloadLinks.push(
        <link
          key={`preload-${screen.key}-${i}`} // unique key
          rel="preload"
          as="image"
          fetchpriority="high"
          href={buildCloudflareUrl({
            src: image.imageUrl,
            width: imageSize(widths, 'xs'),
            height: imageSize(heights, 'xs'),
            gravity,
            format: 'webp',
            fit,
            quality,
          })}
          imageSrcSet={image.isSvg ? srcSet.join(', ') : srcSetWebp.join(', ')}
          media={media}
        />,
      );
    }
  });

  return (
    <>
      <Head>{preloadLinks}</Head>

      <picture
        className={classNames('h-full w-full', className, !preload && !dataNotLazy && 'lazy-image')}
        data-not-lazy={dataNotLazy}
        ref={pictureRef}
        {...props}
      >
        {sourceArray}

        <img
          {...(preload || dataNotLazy
            ? {
                src: buildCloudflareUrl({
                  src: image.imageUrl,
                  width: imageSize(widths, 'lg'),
                  height: imageSize(heights, 'lg'),
                  gravity,
                  fit,
                  quality,
                }),
              }
            : {
                'data-src': buildCloudflareUrl({
                  src: image.imageUrl,
                  width: imageSize(widths, 'lg'),
                  height: imageSize(heights, 'lg'),
                  gravity,
                  fit,
                  quality,
                }),
                src: image.blurHash || 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==',
              })}
          alt={image.imageAlt || ''}
          className="block h-full w-full object-cover"
          style={{ objectPosition }}
          {...imgProps}
        />
      </picture>
    </>
  );
};

ResponsiveImage.propTypes = {
  image: PropTypes.object,
  widths: PropTypes.object,
  heights: PropTypes.object,
  preload: PropTypes.bool,
  cdnProps: PropTypes.object,
  dataNotLazy: PropTypes.bool,
  imgProps: PropTypes.object,
  className: PropTypes.string,
  fit: PropTypes.string,
  quality: PropTypes.number,
};

export default ResponsiveImage;
